import type { ReactNode } from 'react';
import { FieldError } from '@/components/ui/field-error';
import { FormSection } from '@/components/ui/form-section';
import { StatusChip } from '@/components/ui/status-chip';
import { isFiniteNumber } from '@/utils/number';
import { FIELD_DESCRIPTIONS } from './field-descriptions';
import { IndexSourcePicker } from './index-source-picker';
import {
  COMPONENT_LABELS,
  NEW_OUT_OF_BOOK,
  NEW_VOLATILITY,
  type OutOfBookInput,
  patchRules,
  type RulesInput,
  setRuleField,
  setRuleLive,
  type VolatilityInput,
} from './mm-program-editing';
import { CheckboxField, NumberField, NumberListField } from './mm-program-fields';
import type { ProgramInput, RuleComponent } from './mm-program-schema';
import { OutOfBookChart, VolatilityLadderChart } from './program-charts';

const componentReady = (rules: RulesInput | undefined, component: RuleComponent) => {
  if (!rules) return false;
  if (component === 'rest') return isFiniteNumber(rules.place_rest_ms) && isFiniteNumber(rules.move_rest_ms);
  if (component === 'linger') return isFiniteNumber(rules.fill_linger_ms);
  if (component === 'skew') return isFiniteNumber(rules.max_skew_fraction);
  if (component === 'vol') return rules.volatility !== undefined;
  return rules.out_of_book !== undefined;
};

interface RuleBlockProps {
  component: RuleComponent;
  rules: RulesInput | undefined;
  onLiveChange: (enabled: boolean) => void;
  children: ReactNode;
}

const RuleBlock = ({ component, rules, onLiveChange, children }: RuleBlockProps) => {
  const isLive = rules?.live.includes(component) ?? false;
  const isSet = componentReady(rules, component);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold">{COMPONENT_LABELS[component]}</h4>
          <StatusChip className="px-2 py-0.5 text-xs" variant={isLive ? 'success' : 'muted'}>
            {isLive ? 'Live' : isSet ? 'Backtest only' : 'Not set'}
          </StatusChip>
        </div>
        <CheckboxField checked={isLive} description={FIELD_DESCRIPTIONS.live} disabled={!isLive && !isSet} label="Live in production" onChange={onLiveChange} />
      </div>
      {children}
    </div>
  );
};
RuleBlock.displayName = 'RuleBlock';

interface ProgramRulesFormProps {
  instrument: string;
  /** Rules are shared by a session set, so they are read from its first program */
  shared: ProgramInput;
  saved?: ProgramInput;
  updateAll: (update: (program: ProgramInput) => ProgramInput) => void;
  sharedProblem: (field: string) => string | undefined;
  status?: ReactNode;
}

export const ProgramRulesForm = ({ instrument, shared, saved, updateAll, sharedProblem, status }: ProgramRulesFormProps) => {
  const rules = shared.rules;
  const savedRules = saved?.rules;
  // Session sets differ in bucket widths, so widened widths are only shown for a single schedule.
  const hasOneWidth = shared.session === undefined && isFiniteNumber(shared.b1.bps) && isFiniteNumber(shared.b2.bps);

  const setRule = <K extends Exclude<keyof RulesInput, 'live'>>(field: K, value: RulesInput[K] | undefined) =>
    updateAll((program) => patchRules(program, (current) => setRuleField(current, field, value)));

  const setVolatility = (patch: Partial<VolatilityInput>) =>
    updateAll((program) => patchRules(program, (current) => ({ ...current, volatility: { ...NEW_VOLATILITY, ...current.volatility, ...patch } })));

  const setOutOfBook = (patch: Partial<OutOfBookInput>) =>
    updateAll((program) => patchRules(program, (current) => ({ ...current, out_of_book: { ...NEW_OUT_OF_BOOK, ...current.out_of_book, ...patch } })));

  /** Adds or removes an optional rules block; removing it also takes its component out of live. */
  const toggleRuleBlock = (field: 'volatility' | 'out_of_book', component: RuleComponent, enabled: boolean) =>
    updateAll((program) =>
      patchRules(program, (current) => ({
        ...setRuleField(current, field, enabled ? (rules?.[field] ?? (field === 'volatility' ? NEW_VOLATILITY : NEW_OUT_OF_BOOK)) : undefined),
        live: enabled ? current.live : current.live.filter((item) => item !== component),
      })),
    );

  const setLive = (component: RuleComponent, enabled: boolean) => updateAll((program) => setRuleLive(program, component, enabled));

  return (
    <FormSection status={status} title="Rules">
      <div className="grid gap-x-8 gap-y-8 xl:grid-cols-3">
        <RuleBlock component="rest" onLiveChange={(enabled) => setLive('rest', enabled)} rules={rules}>
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              description={FIELD_DESCRIPTIONS.place_rest_ms}
              error={sharedProblem('rules.place_rest_ms')}
              label="Place rest"
              onChange={(value) => setRule('place_rest_ms', value)}
              unit="ms"
              value={rules?.place_rest_ms}
            />
            <NumberField
              description={FIELD_DESCRIPTIONS.move_rest_ms}
              error={sharedProblem('rules.move_rest_ms')}
              label="Move rest"
              onChange={(value) => setRule('move_rest_ms', value)}
              unit="ms"
              value={rules?.move_rest_ms}
            />
          </div>
        </RuleBlock>
        <RuleBlock component="linger" onLiveChange={(enabled) => setLive('linger', enabled)} rules={rules}>
          <NumberField
            description={FIELD_DESCRIPTIONS.fill_linger_ms}
            error={sharedProblem('rules.fill_linger_ms')}
            label="Fill linger"
            onChange={(value) => setRule('fill_linger_ms', value)}
            unit="ms"
            value={rules?.fill_linger_ms}
          />
        </RuleBlock>
        <RuleBlock component="skew" onLiveChange={(enabled) => setLive('skew', enabled)} rules={rules}>
          <NumberField
            description={FIELD_DESCRIPTIONS.max_skew_fraction}
            error={sharedProblem('rules.max_skew_fraction')}
            label="Max skew"
            onChange={(value) => setRule('max_skew_fraction', value)}
            scale={100}
            unit="%"
            value={rules?.max_skew_fraction}
          />
        </RuleBlock>
      </div>

      <div className="border-t pt-6">
        <RuleBlock component="vol" onLiveChange={(enabled) => setLive('vol', enabled)} rules={rules}>
          <CheckboxField
            checked={rules?.volatility !== undefined}
            description={FIELD_DESCRIPTIONS.volatility}
            label="Widen on volatility"
            onChange={(enabled) => toggleRuleBlock('volatility', 'vol', enabled)}
          />
          {rules?.volatility ? (
            <div className="grid items-start gap-8 xl:grid-cols-2">
              <div className="grid content-start gap-4 sm:grid-cols-2">
                <NumberListField
                  description={FIELD_DESCRIPTIONS['volatility.rungs_bps']}
                  error={sharedProblem('rules.volatility.rungs_bps')}
                  label="Rungs"
                  onChange={(rungs_bps) => setVolatility({ rungs_bps })}
                  unit="bps"
                  value={rules.volatility.rungs_bps}
                />
                <NumberListField
                  description={FIELD_DESCRIPTIONS['volatility.add_bps']}
                  error={sharedProblem('rules.volatility.add_bps')}
                  label="Added width"
                  onChange={(add_bps) => setVolatility({ add_bps })}
                  unit="bps"
                  value={rules.volatility.add_bps}
                />
                <NumberField
                  description={FIELD_DESCRIPTIONS['volatility.cooldown_s']}
                  error={sharedProblem('rules.volatility.cooldown_s')}
                  label="Cooldown"
                  onChange={(cooldown_s) => setVolatility({ cooldown_s: cooldown_s ?? Number.NaN })}
                  unit="s"
                  value={rules.volatility.cooldown_s}
                />
                <IndexSourcePicker
                  description={FIELD_DESCRIPTIONS['volatility.source']}
                  error={sharedProblem('rules.volatility.provider') ?? sharedProblem('rules.volatility.instrument')}
                  instrument={instrument}
                  label="Volatility source"
                  onChange={({ provider, symbol }) => setVolatility({ provider, instrument: symbol })}
                  provider={rules.volatility.provider}
                  symbol={rules.volatility.instrument}
                />
              </div>
              <VolatilityLadderChart baseWidths={hasOneWidth ? { b1: shared.b1.bps, b2: shared.b2.bps } : undefined} draft={rules.volatility} saved={savedRules?.volatility} />
            </div>
          ) : null}
        </RuleBlock>
      </div>

      <div className="border-t pt-6">
        <RuleBlock component="oob" onLiveChange={(enabled) => setLive('oob', enabled)} rules={rules}>
          <CheckboxField
            checked={rules?.out_of_book !== undefined}
            description={FIELD_DESCRIPTIONS.out_of_book}
            label="Penalise time out of the book"
            onChange={(enabled) => toggleRuleBlock('out_of_book', 'oob', enabled)}
          />
          {rules?.out_of_book ? (
            <div className="grid items-start gap-8 xl:grid-cols-2">
              <div className="grid content-start gap-4 sm:grid-cols-2">
                <NumberField
                  description={FIELD_DESCRIPTIONS['out_of_book.min_gap_s']}
                  error={sharedProblem('rules.out_of_book.min_gap_s')}
                  label="Minimum gap"
                  onChange={(min_gap_s) => setOutOfBook({ min_gap_s: min_gap_s ?? Number.NaN })}
                  unit="s"
                  value={rules.out_of_book.min_gap_s}
                />
                <NumberField
                  description={FIELD_DESCRIPTIONS['out_of_book.full_s']}
                  error={sharedProblem('rules.out_of_book.full_s')}
                  label="Full cut after"
                  onChange={(full_s) => setOutOfBook({ full_s: full_s ?? Number.NaN })}
                  unit="s"
                  value={rules.out_of_book.full_s}
                />
                <NumberField
                  description={FIELD_DESCRIPTIONS['out_of_book.max_cut_fraction']}
                  error={sharedProblem('rules.out_of_book.max_cut_fraction')}
                  label="Maximum cut"
                  onChange={(max_cut_fraction) => setOutOfBook({ max_cut_fraction: max_cut_fraction ?? Number.NaN })}
                  scale={100}
                  unit="%"
                  value={rules.out_of_book.max_cut_fraction}
                />
              </div>
              <OutOfBookChart draft={rules.out_of_book} saved={savedRules?.out_of_book} />
            </div>
          ) : null}
        </RuleBlock>
      </div>
      <FieldError message={sharedProblem('rules.live')} />
    </FormSection>
  );
};
ProgramRulesForm.displayName = 'ProgramRulesForm';
